import React from 'react';

/**
 * Lenvoy lockup, variant 1B (angular car profile, offset amber triangle, bridge
 * dot), drawn inline so the wordmark uses the page's Manrope web font instead
 * of the SVG fallback. Geometry from `lenvoy_design/assets/core`.
 *
 * `tone="light"` is the mark on light backgrounds (navy car); `tone="dark"` is
 * the reversed mark for navy surfaces (paper car). The triangle and dot stay amber.
 */
export interface LenvoyLogoProps {
  tone: 'light' | 'dark';
  /** Pixel height of the mark; the wordmark scales with it. */
  height?: number;
  /** Mark only, no wordmark. */
  markOnly?: boolean;
  className?: string;
}

const NAVY = '#1B2A4A';
const PAPER = '#F4F1EA';
const AMBER = '#F2A93B';

export function LenvoyMark({ tone, height = 32 }: { tone: 'light' | 'dark'; height?: number }) {
  // viewBox from the shipped mark asset (150.6 × 68.24).
  const width = (height * 150.6) / 68.24;
  return (
    <svg
      viewBox="0 0 150.6 68.24"
      width={width}
      height={height}
      role="img"
      aria-label="Lenvoy"
      focusable="false"
    >
      <g transform="translate(12.6 -3.76)">
        <path
          d="M26,48 L56,48 L72,28 L104,28 L116,48 L132,48 L132,66 L114,66 L109,56 L97,56 L92,66 L52,66 L47,56 L35,56 L30,66 L18,66 Z"
          fill={tone === 'dark' ? PAPER : NAVY}
        />
        <polygon points="15.8,41.76 -6.6,27.36 4.6,9.76" fill={AMBER} />
        <circle cx="21.5" cy="44.5" r="4.2" fill={AMBER} />
      </g>
    </svg>
  );
}

export default function LenvoyLogo({ tone, height = 32, markOnly, className }: LenvoyLogoProps) {
  const color = tone === 'dark' ? PAPER : NAVY;
  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: Math.round(height * 0.28) }}
    >
      <LenvoyMark tone={tone} height={height} />
      {!markOnly && (
        <span
          style={{
            fontFamily: "'Manrope', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 600,
            fontSize: Math.round(height * 0.95),
            letterSpacing: '-0.025em',
            lineHeight: 1,
            color,
            whiteSpace: 'nowrap',
          }}
        >
          Lenvoy
        </span>
      )}
    </span>
  );
}
